import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from './protected-route'
import { AppLayout } from '@/components/layout/AppLayout'
import { ROLES } from '@/constants'
import { getDefaultRouteForRole } from '@/constants/navigation'
import { useAuth } from '@/hooks/use-auth'

const LandingView = lazy(() => import('@/views/landing/LandingView'))
const LoginView = lazy(() => import('@/views/auth/LoginView'))
const ForbiddenView = lazy(() => import('@/views/auth/ForbiddenView'))
const DashboardView = lazy(() => import('@/views/dashboard/DashboardView'))
const ProfileView = lazy(() => import('@/views/profile/ProfileView'))
const GraphsView = lazy(() => import('@/views/graphs/GraphsView'))
const ProfessionsView = lazy(() => import('@/views/catalogs/ProfessionsView'))
const HolidaysView = lazy(() => import('@/views/catalogs/HolidaysView'))
const UsersView = lazy(() => import('@/views/settings/UsersView'))
const RolesView = lazy(() => import('@/views/settings/RolesView'))
const SecurityMonitorView = lazy(() => import('@/views/settings/SecurityMonitorView'))
const ActivityView = lazy(() => import('@/views/settings/ActivityView'))
const ProfessionalsView = lazy(() => import('@/views/professionals/ProfessionalsView'))
const PatientsView = lazy(() => import('@/views/patients/PatientsView'))
const AgendaView = lazy(() => import('@/views/agenda/AgendaView'))
const AppointmentsEntryView = lazy(() => import('@/views/appointments/AppointmentsEntryView'))
const AssistantView = lazy(() => import('@/views/assistant/AssistantView'))
const PrivacyPolicyView = lazy(() => import('@/views/legal/PrivacyPolicyView'))
const TermsView = lazy(() => import('@/views/legal/TermsView'))
const LimaCurriculumView = lazy(() => import('@/views/career/LimaCurriculumView'))
const GuzmanCurriculumView = lazy(() => import('@/views/career/GuzmanCurriculumView'))

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  )
}

// Replaces the old hardcoded "/dashboard" fallback — Dashboard is now
// Admin/Secretaria-only, so an unmatched path must land each role on a
// page it can actually open instead of bouncing straight to /403.
function DefaultRouteRedirect() {
  const { user } = useAuth()
  return <Navigate to={user ? getDefaultRouteForRole(user.menuSections) : '/profile'} replace />
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingView />} />
        <Route path="/login" element={<LoginView />} />
        <Route path="/403" element={<ForbiddenView />} />
        <Route path="/asistente" element={<AssistantView />} />
        <Route path="/politica-de-privacidad" element={<PrivacyPolicyView />} />
        <Route path="/terminos-y-condiciones" element={<TermsView />} />
        <Route path="/lima-curriculum" element={<LimaCurriculumView />} />
        <Route path="/guzman-curriculum" element={<GuzmanCurriculumView />} />

        {/* Protected layout routes — AppLayout renders <Outlet /> for children */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA]}><DashboardView /></ProtectedRoute>} />
          <Route path="profile" element={<ProfileView />} />
          <Route path="agenda" element={<ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL]}><AgendaView /></ProtectedRoute>} />
          <Route path="turnos" element={<ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL, ROLES.PATIENT]}><AppointmentsEntryView /></ProtectedRoute>} />
          <Route path="graficos" element={<ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL]}><GraphsView /></ProtectedRoute>} />
          <Route path="catalogos" element={<Navigate to="/catalogos/profesiones" replace />} />
          <Route
            path="catalogos/profesiones"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA]}>
                <ProfessionsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="catalogos/feriados"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA]}>
                <HolidaysView />
              </ProtectedRoute>
            }
          />
          <Route
            path="profesionales"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA]}>
                <ProfessionalsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="pacientes"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL]}>
                <PatientsView />
              </ProtectedRoute>
            }
          />
          <Route path="settings" element={<Navigate to="/settings/users" replace />} />
          <Route
            path="settings/users"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN]}>
                <UsersView />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/roles"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN]}>
                <RolesView />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/security-monitor"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN]}>
                <SecurityMonitorView />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/activity"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN]}>
                <ActivityView />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<DefaultRouteRedirect />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
